import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { OrgBrand } from "@/components/OrgBrand";
import {
  TestCaseViewer,
  type SeriesRailState,
} from "@/components/tests/TestCaseViewer";
import { TestCountdown } from "@/components/tests/TestCountdown";
import { CandidateApplicationForm } from "@/components/tests/CandidateApplicationForm";
import { TestRecorder } from "@/components/tests/TestRecorder";
import { TestStartGate } from "@/components/tests/TestStartGate";
import { AppDialog } from "@/components/ui/AppDialog";
import { useTestCountdown } from "@/hooks/useTestCountdown";
import { useToast } from "@/hooks/use-toast";
import {
  beginAssessment,
  completeIntro,
  exitAssessment,
  finishDryRun,
  heartbeat,
  openCase,
  recordIntegrityEvent,
  startDryRun,
  submitAttempt,
  useAttempt,
  type AttemptCase,
} from "@/api/tests-api";

const HEARTBEAT_MS = 30_000;

/**
 * The sitting.
 *
 * Four states, in order, and the clock only runs in the last two:
 *
 *   intro  →  start gate (+ optional practice run)  →  assessment  →  done
 *
 * The gate is the point. Opening the exam used to start the countdown,
 * so a candidate lost assessment time to reading the instructions.
 * Nothing runs now until they deliberately press "Start the test".
 */
export function TestAttemptPage() {
  const { attemptId = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const attempt = useAttempt(attemptId);
  const data = attempt.data;

  const [selected, setSelected] = useState<string | null>(null);
  const [showIntro, setShowIntro] = useState(true);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  // Which irreversible action is being confirmed, if any. Replaces two
  // `window.confirm` calls -- see AppDialog for why.
  const [confirming, setConfirming] = useState<"submit" | "exit" | null>(null);
  // The sealed case a recording just landed for, which drives the
  // "recorded — what next?" dialog. Null when that dialog is closed.
  const [recordedFor, setRecordedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setShowIntro(!data.intro_completed);
    if (data.started && data.status !== "IN_PROGRESS") setFinished(true);
  }, [data]);

  const onExpire = useCallback(() => {
    setFinished(true);
    toast({
      title: "Time is up",
      description: "Your assessment has ended and your work has been saved.",
    });
  }, [toast]);

  const inDryRun = !!data?.dry_run_started && !data?.dry_run_finished;
  const inAssessment = !!data?.started && !finished;

  // Whichever clock is running drives the countdown — only one ever is,
  // because beginning the assessment closes any open practice run.
  const liveSeconds = inAssessment
    ? (data?.seconds_remaining ?? null)
    : inDryRun
      ? (data?.dry_run_seconds_remaining ?? null)
      : null;

  const countdown = useTestCountdown(
    liveSeconds,
    inAssessment ? onExpire : undefined,
    // The two clocks are different deadlines; switching between them has
    // to re-anchor, or the assessment inherits the practice countdown.
    inAssessment ? "assessment" : inDryRun ? "practice" : "idle",
  );
  const syncRef = useRef(countdown.syncFromServer);
  useEffect(() => {
    syncRef.current = countdown.syncFromServer;
  }, [countdown.syncFromServer]);

  useEffect(() => {
    if (!attemptId || !inAssessment) return undefined;
    const id = window.setInterval(() => {
      void heartbeat(attemptId)
        .then((tick) => {
          syncRef.current(tick.seconds_remaining);
          if (tick.status !== "IN_PROGRESS") setFinished(true);
        })
        .catch(() => {
          /* transient — the next beat retries */
        });
    }, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [attemptId, inAssessment]);

  // Integrity signals: context for a reviewer, never a gate. Only during
  // the assessment — nobody cares what a candidate does while practising.
  useEffect(() => {
    if (!attemptId || !inAssessment) return undefined;
    const onHide = () => {
      if (document.visibilityState === "hidden") {
        void recordIntegrityEvent(attemptId, "tab_hidden");
      }
    };
    const onBlur = () => void recordIntegrityEvent(attemptId, "window_blur");
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
    };
  }, [attemptId, inAssessment]);

  const cases = useMemo<AttemptCase[]>(
    () => (inDryRun ? (data?.dry_run_cases ?? []) : (data?.cases ?? [])),
    [data, inDryRun],
  );
  // 1 application + (practice) + assessment. `has_dry_run` is per exam.
  const totalSteps = data?.has_dry_run ? 3 : 2;
  const assessmentStep = totalSteps;

  const current = cases.find((c) => c.assignment_case_id === selected) ?? null;
  const recordedCount = cases.filter((c) => c.answered).length;
  const unrecorded = cases.length - recordedCount;
  const caseIndex = current
    ? cases.findIndex((c) => c.assignment_case_id === current.assignment_case_id)
    : -1;

  // The SERIES of the case being read, reported up by the viewer rather
  // than derived a second time here. Null until it has rendered once.
  const [rail, setRail] = useState<SeriesRailState | null>(null);

  // HOW MUCH TIME PER CASE STILL TO DO, which is the question a candidate
  // is really asking of the clock. Whole minutes, floored: rounding up
  // would tell somebody with 70 seconds and two cases that they have a
  // minute each.
  const paceLine =
    unrecorded > 0 && countdown.secondsRemaining > 0
      ? `About ${Math.max(
          1,
          Math.floor(countdown.secondsRemaining / 60 / unrecorded),
        )} min per remaining case`
      : unrecorded === 0
        ? "All cases recorded"
        : "";

  // The first-run note, dismissed per sitting. sessionStorage, not
  // local: a candidate who returns to a second exam is a first-timer on
  // it again, and a candidate who reloads mid-sitting is not.
  const [showHelp, setShowHelp] = useState(true);

  // The "recorded — what next?" dialog reads off the CASE LIST rather
  // than off the id alone, so it closes by itself if the phase changes
  // underneath it (finishing practice swaps the whole list).
  const justRecorded =
    cases.find((c) => c.assignment_case_id === recordedFor) ?? null;
  const nextCase = justRecorded
    ? (cases[cases.findIndex(
        (c) => c.assignment_case_id === justRecorded.assignment_case_id,
      ) + 1] ?? null)
    : null;

  // Land ON the first case rather than on an empty panel.
  //
  // Two jobs in one effect, and they have to be one: changing phase must
  // not leave a case from the OTHER phase selected, and arriving with
  // nothing selected showed a blank right-hand side that looked broken —
  // a candidate under a clock had to work out that the list was
  // clickable. Selecting the first case of whichever phase is running
  // covers both, and re-runs when `cases` changes because the practice
  // and assessment lists arrive at different moments.
  useEffect(() => {
    if (cases.length === 0) {
      setSelected(null);
      return;
    }
    const stillHere = cases.some((c) => c.assignment_case_id === selected);
    if (stillHere) return;
    // Land on the first case STILL TO RECORD, not blindly on the first.
    // A candidate resuming after a closed tab or a dropped connection
    // came back to case 1 with a tick already on it, and had to work out
    // for themselves where they had got to -- under a clock that never
    // stopped. Falls back to the first case when everything is done,
    // which is the right place to start a review pass.
    const next = cases.find((c) => !c.answered) ?? cases[0]!;
    setSelected(next.assignment_case_id);
  }, [cases, selected]);

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      await attempt.refetch();
    } catch (err) {
      // SHOW THE SERVER'S REASON. A bare `catch {}` here turned a
      // specific 422 -- "Please answer: <a question the candidate could
      // not see>" -- into "Could not move on to the assessment", and
      // that cost a diagnosis. The fallback is for a network failure,
      // which genuinely has nothing to say.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? failure;
      toast({ title: detail, variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const pickCase = async (row: AttemptCase) => {
    setSelected(row.assignment_case_id);
    try {
      await openCase(attemptId, row.assignment_case_id);
    } catch {
      /* opening is a bookmark, not a gate */
    }
  };

  /** The application form has already saved itself; this only marks the
   *  step done on the attempt, which is what survives a reload. */
  const finishIntro = () =>
    act(async () => {
      await completeIntro(attemptId);
      setShowIntro(false);
    }, "Could not move on to the assessment");

  const doSubmit = () =>
    act(async () => {
      await submitAttempt(attemptId);
      setFinished(true);
    }, "Could not submit");

  const doExit = () =>
    act(async () => {
      await exitAssessment(attemptId);
      setFinished(true);
    }, "Could not exit");

  /**
   * A take landed. Refetch first, THEN offer where to go next.
   *
   * Order matters: the dialog names the next case and, on the last one,
   * offers to submit -- both read off a case list that must already
   * include the tick this recording just earned. Offering "submit" while
   * the panel behind still says "1 of 2 still to record" is the kind of
   * disagreement that makes a candidate under a clock stop and re-check.
   */
  const handleRecorded = async (assignmentCaseId: string) => {
    await attempt.refetch();
    setRecordedFor(assignmentCaseId);
  };

  if (attempt.isLoading) {
    return (
      <Shell>
        <p data-cy="attempt-loading" className="text-sm text-foreground-muted">
          Loading your assessment…
        </p>
      </Shell>
    );
  }

  if (attempt.isError || !data) {
    return (
      <Shell>
        <div className="text-center">
          <p data-cy="attempt-error" className="text-sm text-status-urgent">
            This assessment could not be opened.
          </p>
          <button
            type="button"
            onClick={() => navigate("/tests")}
            className="sk-btn sk-btn-primary mt-4"
          >
            Back to my assessments
          </button>
        </div>
      </Shell>
    );
  }

  if (finished) {
    return (
      <Shell>
        <div
          data-cy="attempt-finished"
          className="max-w-md rounded-xl border border-border bg-background-card p-8 text-center"
        >
          <h1 className="text-lg font-semibold">Assessment complete</h1>
          <p className="mt-2 text-sm text-foreground-muted">
            Thank you. Your work has been submitted and is with the clinic for
            review. There is nothing further for you to do here.
          </p>
          <button
            type="button"
            data-cy="btn-back-to-tests"
            onClick={() => navigate("/tests")}
            className="sk-btn sk-btn-primary mt-6"
          >
            Back to my assessments
          </button>
        </div>
      </Shell>
    );
  }

  // ---- step 1: the application form ----------------------------------
  //
  // STATIC, and saving to its own table. The dynamic question engine this
  // replaced is still on disk and still works -- see CLAUDE.md, "Inert
  // code kept on purpose".
  if (showIntro) {
    return (
      <main
        className="flex min-h-screen flex-col bg-background text-foreground"
        data-cy="test-attempt-page"
      >
        <PlainHeader title={data.title} step={1} total={totalSteps} />
        <section className="flex-1 px-6 py-8">
          <CandidateApplicationForm
            prompt={data.intro_prompt}
            onContinue={() => void finishIntro()}
          />
        </section>
      </main>
    );
  }

  // ---- step 2: the gate. Nothing is running here. ---------------------
  if (!inAssessment && !inDryRun) {
    return (
      <main
        className="flex min-h-screen flex-col bg-background text-foreground"
        data-cy="test-attempt-page"
      >
        <PlainHeader title={data.title} />
        <TestStartGate
          attempt={data}
          busy={busy}
          onStartDryRun={() =>
            void act(
              () => startDryRun(attemptId),
              "Could not start the practice run",
            )
          }
          onSkipDryRun={() =>
            void act(() => finishDryRun(attemptId, true), "Could not skip")
          }
          onBeginAssessment={() =>
            void act(
              () => beginAssessment(attemptId),
              "Could not start the test",
            )
          }
        />
      </main>
    );
  }

  // ---- step 3: working — practising, or being assessed ---------------
  return (
    <main
      className="flex min-h-screen flex-col bg-sitting-ground text-foreground"
      data-cy="test-attempt-page"
    >
      {/* THE HEADER, to the design's own metrics: brand and exam title
          on the baseline at the left, then a right group of a labelled
          readout and the clock in its own bordered plate. The label was
          missing entirely and the group had no structure -- a countdown
          with nothing naming it is a number. */}
      <header
        data-cy="topbar"
        className="flex flex-wrap items-center gap-4 border-b border-border bg-background-card px-5 py-3"
      >
        <div className="flex min-w-0 items-baseline gap-2.5">
          {/* The logo when the organisation has uploaded one, its name
              when it has not. OrgBrand decides; nothing here hard-codes
              a practice. */}
          <OrgBrand variant="topbar" />
          <span className="sk-sit-body truncate text-foreground-muted">
            {data.title}
          </span>
          <StepBadge
            step={inDryRun ? 2 : assessmentStep}
            total={totalSteps}
          />
          {inDryRun && (
            <span
              data-cy="practice-badge"
              className="shrink-0 rounded-full bg-status-new-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-status-new"
            >
              Practice — not counted
            </span>
          )}
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-foreground-muted">
              {inDryRun ? "Practice time" : "Time remaining"}
            </p>
            {paceLine && (
              <p
                data-cy="pace-line"
                className="sk-sit-note text-foreground-muted"
              >
                {paceLine}
              </p>
            )}
          </div>
          <TestCountdown
            secondsRemaining={countdown.secondsRemaining}
            muted={inDryRun}
          />
        </div>
      </header>

      {/* FIRST TIME HERE. Dismissed for the sitting, not for ever: a
          candidate sitting a second exam is a first-timer on it again,
          and one who reloads mid-sitting is not being told anything they
          have not just read. */}
      {showHelp && !inDryRun && (
        <div
          data-cy="sitting-help"
          className="flex items-start gap-3.5 border-b border-sitting-note-border bg-sitting-note-bg px-5 py-3"
        >
          <span
            aria-hidden
            className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-status-new text-sm font-bold text-white"
          >
            i
          </span>
          <p className="sk-sit-lead min-w-0 flex-1 font-normal text-foreground">
            <strong className="font-bold">First time sitting this exam?</strong>{" "}
            Work down the case list on the left. For each case, open the series
            you need, then record one spoken report. Nothing is final until you
            press Submit — you can return to any case.
          </p>
          <button
            type="button"
            data-cy="btn-dismiss-help"
            onClick={() => setShowHelp(false)}
            className="sk-sit-row shrink-0 rounded-lg border border-status-new/40 bg-background-card px-3 py-1.5 text-status-new hover:bg-table-row-hover"
          >
            Got it
          </button>
        </div>
      )}

      {/* FOUR COLUMNS OF CARDS ON THE PAGE GROUND, which is what the
          design is and what this was not: the panes were flush panels
          divided by hairlines, so the screen read as one slab. Cards on
          --background separate the four jobs -- choose a case, choose a
          series, read, report -- without a rule between every pair.

          `flex-wrap` and the min-widths are the design's own: below about
          1300px the report column drops under the images rather than
          crushing them. */}
      <section
        data-cy="sitting-columns"
        className="flex flex-1 flex-wrap items-stretch gap-3.5 overflow-auto bg-sitting-ground px-5 pb-6 pt-3.5"
      >
        {/* ---- Cases, and the way out --------------------------------- */}
        <nav
          data-cy="test-case-nav"
          className="flex w-[230px] min-w-[210px] flex-[0_0_230px] flex-col gap-3"
        >
          <section className="flex flex-col overflow-hidden rounded-xl border border-border bg-background-card">
            <div className="px-3.5 pb-3 pt-3.5">
              <p className="sk-sit-eyebrow text-foreground-muted">
                {inDryRun ? "Practice cases" : "Cases"}
              </p>
              <p
                data-cy="cases-recorded-line"
                className="sk-sit-lead mt-2 text-foreground"
              >
                {recordedCount} of {cases.length} recorded
              </p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-background-subtle">
                <div
                  data-cy="cases-progress"
                  className="h-full rounded-full bg-accent transition-[width]"
                  style={{
                    width: `${
                      cases.length
                        ? Math.round((recordedCount / cases.length) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
            <ul className="border-t border-border">
              {cases.map((row) => {
                const isActive = selected === row.assignment_case_id;
                return (
                  <li key={row.assignment_case_id}>
                    <button
                      type="button"
                      data-cy="test-case-item"
                      onClick={() => void pickCase(row)}
                      className={[
                        "flex w-full items-start gap-2.5 border-l-[3px] px-3.5 py-2.5 text-left transition-colors",
                        isActive
                          ? "border-status-new bg-status-new-bg/60"
                          : "border-transparent hover:bg-table-row-hover",
                      ].join(" ")}
                    >
                      {/* GREEN IS RECORDED, BLUE IS THE ONE OPEN, and
                          they are different questions. Tinting the open
                          case green made an unrecorded case somebody
                          happened to be reading look finished. */}
                      <span
                        className={[
                          "mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full [font-size:12px] font-bold tabular-nums",
                          row.answered
                            ? "bg-accent text-accent-text"
                            : isActive
                              ? "bg-status-new text-white"
                              : "bg-background-subtle text-foreground-muted",
                        ].join(" ")}
                      >
                        {row.answered ? (
                          <Check aria-hidden className="h-3 w-3" />
                        ) : (
                          row.position + 1
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          data-cy="case-title"
                          className="sk-sit-row block truncate text-foreground"
                        >
                          {row.title}
                        </span>
                        {/* ONLY WHEN THERE IS SOMETHING TO SAY. An empty
                            status still drew the element, which left a
                            stray line of spacing under the title. */}
                        {(row.answered || !inDryRun) && (
                          <span
                            data-cy="case-status"
                            className={[
                              "sk-sit-meta mt-0.5 block",
                              row.answered
                                ? "font-semibold text-accent-hover"
                                : "text-foreground-muted",
                            ].join(" ")}
                          >
                            {row.answered ? "Report recorded" : "Not recorded yet"}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <div className="flex-1" />

          <section className="rounded-xl border border-border bg-background-card p-3.5">
            {inDryRun ? (
              <>
                <button
                  type="button"
                  data-cy="btn-finish-dry-run"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => finishDryRun(attemptId, false),
                      "Could not end the practice run",
                    )
                  }
                  className="sk-sit-action w-full rounded-[10px] bg-accent px-3.5 py-3 text-accent-text hover:bg-accent-hover disabled:opacity-50"
                >
                  Finish practice
                </button>
                <p className="sk-sit-note mt-2.5 text-foreground-muted">
                  None of this is counted. You go back to the start screen
                  and begin the assessment when you are ready.
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-cy="btn-submit-attempt"
                  disabled={busy}
                  onClick={() => setConfirming("submit")}
                  className="w-full rounded-[10px] bg-accent px-3.5 py-3 text-[15px] font-bold text-accent-text hover:bg-accent-hover disabled:opacity-50"
                >
                  Review &amp; submit
                </button>
                <p
                  data-cy="submit-hint"
                  className="sk-sit-note mt-2.5 text-foreground-muted"
                >
                  {unrecorded === 0
                    ? "All reports recorded. You can review before you finish."
                    : `${unrecorded} of ${cases.length} ${
                        cases.length === 1 ? "case" : "cases"
                      } still need a recorded report.`}
                </p>
                {/* A LINK, not a second button. The design makes leaving
                    quieter than finishing on purpose -- two buttons of
                    equal weight invite the wrong one. */}
                <div className="mt-3 border-t border-border pt-3">
                  <button
                    type="button"
                    data-cy="btn-exit-attempt"
                    disabled={busy}
                    onClick={() => setConfirming("exit")}
                    title="Ends the assessment — you cannot return to it"
                    className="sk-sit-note text-foreground-muted underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
                  >
                    Leave without submitting
                  </button>
                </div>
              </>
            )}
          </section>
        </nav>

        {/* ---- Series of the case being read -------------------------- */}
        {rail && rail.series.length > 1 && (
          <aside
            data-cy="series-pane"
            className="flex w-[262px] min-w-[240px] flex-[0_0_262px] flex-col overflow-hidden rounded-xl border border-border bg-background-card"
          >
            <div className="border-b border-border px-3.5 pb-2.5 pt-3.5">
              <p className="sk-sit-eyebrow text-foreground-muted">
                Series
              </p>
              <p
                data-cy="series-summary"
                className="sk-sit-body mt-1.5 text-foreground"
              >
                {rail.unseen === 0
                  ? `All ${rail.series.length} series opened`
                  : `${rail.unseen} of ${rail.series.length} series not opened yet`}
              </p>
            </div>
            {/* NO GROUPING AND NO SECOND LINE. The design shows each
                series under a heading (Anatomy / Diffusion / After
                contrast) with a friendly name above the raw one. DICOM
                gives us exactly one string -- SeriesDescription -- and
                nothing that says which group a series is in, so both
                would have to be invented. An invented label on a reading
                screen is worse than a raw one: a radiologist can read
                `t2_haste_tra_p2_320` and cannot know a pretty name for
                it was guessed. */}
            <ul className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-1.5">
              {rail.series.map((s, i) => (
                <li key={s.key}>
                  <button
                    type="button"
                    data-cy="series-tab"
                    data-seen={s.seen ? "true" : "false"}
                    onClick={() => rail.select(i)}
                    title={s.seen ? s.label : `${s.label} — not opened yet`}
                    className={[
                      "my-0.5 flex w-full items-start gap-2.5 rounded-lg border px-2.5 py-2.5 text-left transition-colors",
                      i === rail.index
                        ? "border-status-new bg-status-new-bg"
                        : "border-transparent hover:bg-table-row-hover",
                    ].join(" ")}
                  >
                    <span
                      aria-hidden
                      className={[
                        "mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full [font-size:11px] font-bold leading-none",
                        s.seen
                          ? "bg-accent text-accent-text"
                          : "border-[1.5px] border-border-strong",
                      ].join(" ")}
                    >
                      {s.seen ? "✓" : ""}
                    </span>
                    <span className="sk-sit-row min-w-0 flex-1 truncate text-foreground">
                      {s.label}
                    </span>
                    <span className="sk-sit-meta shrink-0 tabular-nums text-foreground-muted">
                      {s.count}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="sk-sit-meta border-t border-border bg-background-subtle px-3.5 py-2.5 text-foreground-muted">
              A tick means you have opened that series. Ticks are not graded.
            </p>
          </aside>
        )}

        {/* ---- The images --------------------------------------------- */}
        <main className="flex min-w-[340px] flex-[1_1_440px] flex-col gap-3">
          {current ? (
            <>
              <div
                data-cy="test-case-detail"
                className="flex flex-wrap items-center gap-3.5"
              >
                <div className="min-w-0">
                  <h1 className="sk-sit-title text-foreground-heading">
                    {current.title}
                  </h1>
                  <p className="sk-sit-body mt-0.5 text-foreground-muted">
                    {[current.modality, current.body_part]
                      .filter(Boolean)
                      .join(" · ") || "Case"}
                  </p>
                </div>
                <div className="flex-1" />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-cy="btn-prev-case"
                    disabled={caseIndex <= 0}
                    onClick={() => {
                      const prev = cases[caseIndex - 1];
                      if (prev) void pickCase(prev);
                    }}
                    className="sk-sit-row rounded-lg border border-border-strong bg-background-card px-3 py-2 text-foreground hover:bg-table-row-hover disabled:opacity-40"
                  >
                    Previous case
                  </button>
                  <button
                    type="button"
                    data-cy="btn-next-case"
                    disabled={caseIndex < 0 || caseIndex >= cases.length - 1}
                    onClick={() => {
                      const next = cases[caseIndex + 1];
                      if (next) void pickCase(next);
                    }}
                    className="sk-sit-row rounded-lg border border-border-strong bg-background-card px-3 py-2 text-foreground hover:bg-table-row-hover disabled:opacity-40"
                  >
                    Next case
                  </button>
                </div>
              </div>

              {/* The viewer is one card: its toolbar, the black stage and
                  the filmstrip share a border and a radius rather than
                  floating loose on the page. */}
              {/* THE STAGE TAKES THE SPARE HEIGHT. The design fixes it
                  at 16/10, which letterboxes a portrait radiograph into
                  half the width it could have had -- and on a reading
                  screen the image is the thing. The card fills the
                  column instead, with the design's 300px as the floor,
                  so a tall study gets the whole window and a short one
                  still cannot collapse. */}
              <div className="flex min-h-[300px] flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background-card">
                <TestCaseViewer
                  source={{
                    kind: "attempt",
                    attemptId,
                    assignmentCaseId: current.assignment_case_id,
                  }}
                  showSeriesRail={false}
                  onSeries={setRail}
                />
              </div>
            </>
          ) : (
            <p
              data-cy="no-case-selected"
              className="rounded-xl border border-border bg-background-card p-6 text-sm text-foreground-muted"
            >
              Choose a case from the list to begin. You can work through them in
              any order.
            </p>
          )}
        </main>

        {/* ---- Your report -------------------------------------------- */}
        {current && (
          <aside className="flex w-[316px] min-w-[288px] flex-[0_0_316px] flex-col gap-3">
            <section className="rounded-xl border border-border bg-background-card p-4">
              <p className="sk-sit-eyebrow text-foreground-muted">
                Your report
              </p>
              <p className="sk-sit-strong mt-1 text-foreground">
                {current.title}
              </p>

              {/* THE BRIEF, when the case carries one. The design has no
                  slot for it because none of its sample cases had one --
                  but the field is real, an admin types it on the case,
                  and a candidate reporting a case with a supplied history
                  must be shown it. Absent, this draws nothing. */}
              {current.clinical_history && (
                <p
                  data-cy="case-history"
                  className="sk-sit-body mt-3 whitespace-pre-line rounded-lg border border-border bg-background-subtle p-3 text-foreground"
                >
                  {current.clinical_history}
                </p>
              )}

              <div className="mt-3.5">
                <TestRecorder
                  attemptId={attemptId}
                  assignmentCaseId={current.assignment_case_id}
                  hasRecording={current.answered}
                  recordingSeconds={current.recording_seconds}
                  onRecorded={() => {
                    void handleRecorded(current.assignment_case_id);
                  }}
                  onDeleted={() => {
                    void attempt.refetch();
                  }}
                />
              </div>

              {/* WHAT TO SAY, in the place they are about to say it. Two
                  named parts rather than "record your report": somebody
                  who has never sat one of these does not know whether an
                  impression is wanted. */}
              {!current.answered && (
                <>
                  <p className="sk-sit-body mt-3 text-foreground-body">
                    Speak your report aloud. Say it in two parts:
                  </p>
                  <ol className="sk-sit-body mt-2.5 list-decimal pl-5 text-foreground-body">
                    <li>
                      <strong className="font-bold text-foreground">
                        Findings
                      </strong>{" "}
                      — what you see.
                    </li>
                    <li>
                      <strong className="font-bold text-foreground">
                        Impression
                      </strong>{" "}
                      — what it means.
                    </li>
                  </ol>
                </>
              )}

              {/* A NUDGE, NOT A GATE. Recording is still allowed with
                  series unopened -- a candidate may legitimately decide a
                  sequence adds nothing -- but nothing else on the screen
                  would tell them they had missed one. */}
              {rail && rail.unseen > 0 && !current.answered && (
                <p
                  data-cy="unopened-warning"
                  className="sk-sit-note mt-3.5 rounded-lg border border-status-wip-border bg-status-wip-bg px-3 py-2.5 text-status-wip-strong"
                >
                  {rail.unseen}{" "}
                  {rail.unseen === 1 ? "series has" : "series have"} not been
                  opened yet. You can still record, but it is worth a look
                  first.
                </p>
              )}
            </section>

            {/* WHERE YOU ARE — one bar per case, not a single meter. The
                design marks each case individually, so a candidate sees
                which of the nine are done rather than a percentage. */}
            <section className="rounded-xl border border-border bg-background-card px-4 py-3.5">
              <p className="sk-sit-eyebrow text-foreground-muted">
                Where you are
              </p>
              <div
                data-cy="where-you-are"
                className="mt-2.5 flex gap-[5px]"
                aria-hidden
              >
                {cases.map((row) => (
                  <span
                    key={row.assignment_case_id}
                    data-answered={row.answered ? "true" : "false"}
                    className={[
                      "h-2 flex-1 rounded-full",
                      row.answered
                        ? "bg-accent"
                        : selected === row.assignment_case_id
                          ? "bg-status-new"
                          : "bg-border",
                    ].join(" ")}
                  />
                ))}
              </div>
              <p
                data-cy="position-line"
                className="sk-sit-body mt-2.5 text-foreground-body"
              >
                Case {caseIndex + 1} of {cases.length}.{" "}
                {unrecorded === 0
                  ? "Nothing left to record."
                  : `${unrecorded} still to record.`}
              </p>
            </section>
          </aside>
        )}
      </section>

      {/* ---- the irreversible pair, and the one that is not ---------- */}

      {/* BEFORE YOU SUBMIT — the sheet, not a sentence.
          It used to name a COUNT: "3 of 9 cases have no recording yet."
          That tells a candidate they are short and not WHICH, so the way
          to find out was to close the dialog and read down the list.
          Every case is named here with its state, and the ones still
          missing a report are the ones the eye lands on.

          AND SUBMIT IS INERT UNTIL EVERY CASE HAS ONE. Submitting is
          how a candidate says "this is finished", and it cannot mean
          that with cases unreported. The way out of a sitting they do
          not intend to finish is "Leave without submitting", which says
          what it does; running out of time expires it, and an expired
          sitting is reviewed with whatever was recorded. */}
      <AppDialog
        open={confirming === "submit"}
        cy="dialog-submit"
        tone="danger"
        title="Before you submit"
        body={
          <>
            <p data-cy="submit-summary">
              {unrecorded === 0
                ? "Every case has a recorded report. Check the list below, then submit."
                : `You have recorded ${recordedCount} of ${cases.length} ${
                    cases.length === 1 ? "report" : "reports"
                  }. Cases highlighted below are still missing one.`}
            </p>
            <ul
              data-cy="submit-case-list"
              className="mt-3 max-h-[46vh] overflow-y-auto rounded-lg border border-border"
            >
              {cases.map((row) => (
                <li
                  key={row.assignment_case_id}
                  data-cy="submit-case-row"
                  data-answered={row.answered ? "true" : "false"}
                  className={[
                    "flex items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0",
                    row.answered ? "" : "bg-status-wip-bg",
                  ].join(" ")}
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {row.position + 1}. {row.title}
                  </span>
                  <span
                    className={[
                      "shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                      row.answered
                        ? "bg-status-done-bg text-status-done"
                        : "bg-status-wip-bg text-status-wip-strong",
                    ].join(" ")}
                  >
                    {row.answered ? "Report recorded" : "Not recorded yet"}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-foreground-muted">
              Submitting ends the exam. You will not be able to change your
              reports afterwards.
            </p>
          </>
        }
        onDismiss={() => setConfirming(null)}
        actions={[
          {
            label: "Keep working",
            cy: "btn-cancel-submit",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setConfirming(null),
          },
          {
            label: "Submit assessment",
            cy: "btn-confirm-submit",
            variant: "danger",
            disabled: unrecorded > 0 || busy,
            onClick: () => {
              setConfirming(null);
              void doSubmit();
            },
          },
        ]}
      />

      <AppDialog
        open={confirming === "exit"}
        cy="dialog-exit"
        tone="danger"
        title="Leave the assessment?"
        body="This ENDS it. You cannot come back to it later, and any cases you have not recorded will stay unanswered."
        onDismiss={() => setConfirming(null)}
        actions={[
          {
            label: "Leave and end it",
            cy: "btn-confirm-exit",
            variant: "danger",
            onClick: () => {
              setConfirming(null);
              void doExit();
            },
          },
          {
            label: "Stay",
            cy: "btn-cancel-exit",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setConfirming(null),
          },
        ]}
      />

      {/* Recorded — where next?
          The actions differ on the LAST case because "next" has no
          meaning there, and a candidate who has just finished the final
          case is at the one moment the two things they might want are
          "check my earlier ones" and "hand it in". Offering those here
          saves hunting for a submit button in the rail. */}
      <AppDialog
        open={justRecorded !== null}
        cy="dialog-recorded"
        tone="success"
        title={inDryRun ? "Practice recording saved" : "Recording saved"}
        body={
          nextCase
            ? `${justRecorded?.title} is recorded. ${recordedCount} of ${cases.length} done.`
            : recordedCount === cases.length
              ? `That was the last one — all ${cases.length} cases are recorded.`
              : `That was the last case in the list. ${unrecorded} still ${
                  unrecorded === 1 ? "has" : "have"
                } no recording.`
        }
        onDismiss={() => setRecordedFor(null)}
        actions={
          nextCase
            ? [
                {
                  label: `Next: ${truncate(nextCase.title)}`,
                  cy: "btn-recorded-next",
                  variant: "primary",
                  autoFocus: true,
                  onClick: () => {
                    setRecordedFor(null);
                    void pickCase(nextCase);
                  },
                },
                {
                  label: "Stay on this case",
                  cy: "btn-recorded-stay",
                  variant: "ghost",
                  onClick: () => setRecordedFor(null),
                },
              ]
            : [
                {
                  label: "Review from the first case",
                  cy: "btn-recorded-review",
                  variant: "primary",
                  autoFocus: true,
                  onClick: () => {
                    setRecordedFor(null);
                    if (cases[0]) void pickCase(cases[0]);
                  },
                },
                // In practice there is nothing to submit; the equivalent
                // ending is finishing the run, which returns to the gate.
                inDryRun
                  ? {
                      label: "Finish practice",
                      cy: "btn-recorded-finish-practice",
                      onClick: () => {
                        setRecordedFor(null);
                        void act(
                          () => finishDryRun(attemptId, false),
                          "Could not end the practice run",
                        );
                      },
                    }
                  : {
                      label: "Submit assessment",
                      cy: "btn-recorded-submit",
                      onClick: () => {
                        setRecordedFor(null);
                        setConfirming("submit");
                      },
                    },
                {
                  label: "Stay here",
                  cy: "btn-recorded-stay",
                  variant: "ghost",
                  onClick: () => setRecordedFor(null),
                },
              ]
        }
      />
    </main>
  );
}

/** Keep a dialog button on one line. Case titles can be long. */
function truncate(s: string, max = 28): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * Where the candidate is in the sitting.
 *
 * Three phases at most -- the application, the practice run, then the
 * assessment -- and the practice run is OPTIONAL per exam, so the total
 * is 2 or 3 rather than a constant. An exam with no practice that
 * announced "Step 1 of 3" would be promising a step that never comes.
 *
 * Deliberately absent from the START GATE. That screen is not a step;
 * it is the junction where the candidate chooses between practising and
 * beginning, and either number written there would be wrong for one of
 * the two buttons.
 */
function StepBadge({ step, total }: { step: number; total: number }) {
  return (
    <span
      data-cy="step-badge"
      className="shrink-0 rounded-full bg-foreground-on-chrome/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
    >
      Step {step} of {total}
    </span>
  );
}

function PlainHeader({
  title,
  step,
  total,
}: {
  title: string;
  step?: number;
  total?: number;
}) {
  return (
    <header
      data-cy="topbar"
      className="flex items-center gap-4 bg-background-topbar px-6 py-3 text-foreground-on-chrome shadow-theme-sm"
    >
      <OrgBrand variant="topbar" />
      <span className="truncate text-sm font-medium">{title}</span>
      {step && total ? <StepBadge step={step} total={total} /> : null}
    </header>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main
      data-cy="test-attempt-page"
      className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground"
    >
      {children}
    </main>
  );
}
